import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, CheckSquareOffset, ClipboardText, CurrencyJpy, Factory, MagnifyingGlassPlus, SealCheck, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { DashboardZone, ActionTile, ListCard, AllClear, MetricsDisclosure } from '@/components/enterprise/DashboardZone';
import { mktApi, errMsg } from './marketingApi';
import { StatusBadge, jd, yen } from './mktShared';

/**
 * 営業ダッシュボード — decision-driven (Jun 2026).
 * Action zone = queues that a person must move forward, ordered by urgency.
 * 製造中 is a status count with no action attached → 参考指標.
 */
const ACTIONS = [
  { key: 'overdue', label: '納期超過', hint: 'すぐに調整が必要', Icon: Warning, urgent: true, to: '/marketing/orders?overdue=1' },
  { key: 'approval_wait', label: '客先承認待ち', hint: '見積の承認を待っています', Icon: ClipboardText, to: '/marketing/orders?status=quoted,approval_pending' },
  { key: 'approved', label: '製造依頼待ち', hint: '製造へ引き渡し', Icon: SealCheck, to: '/marketing/orders?status=approved' },
  { key: 'kansei_wait', label: '売上確定待ち', hint: '出荷済み・完成処理待ち', Icon: CheckSquareOffset, to: '/marketing/orders?status=completion_pending' },
  { key: 'unpaid_billing', label: '未入金請求', hint: '入金確認が必要', Icon: CurrencyJpy, urgent: true, to: '/marketing/billing' },
  { key: 'inspection', label: '検査・出荷準備', hint: '出荷に向けた確認', Icon: MagnifyingGlassPlus, to: '/marketing/orders?status=qc_pending,qc_passed,ready_to_ship,partially_shipped' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    mktApi.dashboard().then(setData).catch((e) => toast.error(errMsg(e, 'ダッシュボードの取得に失敗しました')));
  }, []);

  const counts = data?.counts || {};
  const nothingToDo = !!data && ACTIONS.every((a) => (Number(counts[a.key]) || 0) === 0);

  return (
    <div className="space-y-8" data-testid="mkt-dashboard-page">
      <PageHeader
        testid="mkt-dashboard-header"
        title="営業ダッシュボード"
        description="対応が必要な受注・請求を優先して表示します"
      />

      <DashboardZone title="対応が必要" hint="クリックすると該当の受注一覧に移動します" testid="mkt-action-zone">
        {nothingToDo ? (
          <AllClear message="対応が必要な受注・請求はありません" testid="mkt-allclear" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {ACTIONS.map((a) => (
              <ActionTile key={a.key} icon={a.Icon} label={a.label} hint={a.hint}
                value={data ? (counts[a.key] ?? 0) : 0} urgent={a.urgent}
                onClick={() => navigate(a.to)} testid={`mkt-kpi-${a.key}`} />
            ))}
          </div>
        )}
      </DashboardZone>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ListCard title="売上確定待ち" icon={CheckSquareOffset} count={(data?.kansei_queue || []).length}
          action={<span className="text-[11px] text-zinc-400">出荷が古い順</span>} testid="mkt-kansei-queue">
          {(data?.kansei_queue || []).length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-zinc-400" data-testid="mkt-kansei-queue-empty">売上確定待ちの受注はありません</p>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {data.kansei_queue.map((o) => (
                <li key={o.id}>
                  <button type="button" onClick={() => navigate(`/marketing/orders/${o.id}`)} data-testid={`mkt-kansei-queue-${o.id}`}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-teal-50/50 transition-colors">
                    <span className="font-mono text-xs font-medium text-zinc-600">{o.order_no}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">{o.title || o.customer_name}</span>
                    <span className="text-[11px] text-zinc-400">出荷 {jd(o.shipped_at)}</span>
                    {o.aging_days != null && (
                      <span className={`rounded-md border px-2 py-0.5 text-[10px] font-medium ${o.aging_days >= 3 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-zinc-100 text-zinc-700 border-zinc-200'}`}>
                        {o.aging_days}日経過
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ListCard>

        <ListCard title="今週の納期" icon={CalendarCheck} count={(data?.upcoming || []).length} testid="mkt-upcoming">
          {(data?.upcoming || []).length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-zinc-400" data-testid="mkt-upcoming-empty">今週が納期の受注はありません</p>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {data.upcoming.map((o) => (
                <li key={o.id}>
                  <button type="button" onClick={() => navigate(`/marketing/orders/${o.id}`)} data-testid={`mkt-upcoming-${o.id}`}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-teal-50/50 transition-colors">
                    <span className="text-xs font-medium tabular-nums text-zinc-700">{jd(o.due_date)}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">{o.title || o.customer_name}</span>
                    <span className="text-[11px] tabular-nums text-zinc-400">{yen(o.total)}</span>
                    <StatusBadge status={o.status} label={o.status_label} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ListCard>
      </div>

      <MetricsDisclosure title="参考指標" testid="mkt-metrics">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
          <button type="button" onClick={() => navigate('/marketing/orders?status=sent_to_seizo,in_production')}
            data-testid="mkt-kpi-production" className="text-left rounded-lg border border-zinc-200 p-3 hover:border-teal-300 transition-colors">
            <p className="flex items-center gap-1.5 text-[11px] text-zinc-500"><Factory size={13} weight="duotone" className="text-zinc-400" />製造中</p>
            <p className="text-lg font-bold text-zinc-900 tabular-nums mt-0.5">{data ? (counts.production ?? 0) : '…'}</p>
          </button>
        </div>
      </MetricsDisclosure>
    </div>
  );
}
